import React, { useState, useEffect, useRef } from 'react';

const CompChatbot = ({ isOpen, onClose }) => {
    const [messages, setMessages] = useState([
        { content: '안녕하세요! 주제를 선택하거나 즐겨찾는 질문을 클릭해보세요!', sender: 'bot' }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTopic, setSelectedTopic] = useState('topic');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const chatMessagesRef = useRef(null);

    // 주제별 즐겨찾는 질문 데이터
    const topicsData = {
        'topic': {
            name: '대회관리',
            favorites: [
                '대회 생성 방법을 알려주세요.',
                '선수 등록 방법을 알려주세요.',
                '대진 생성 방법을 알려주세요.',
                '상장은 어디에서 만드나요?',
                '대회 진행은 어떻게 하나요?',
            ],
            url: 'http://tmnt.co.kr'
        },
    };

    // 스크롤을 맨 아래로
    const scrollToBottom = () => {
        setTimeout(() => {
            if (chatMessagesRef.current) {
                chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
            }
        }, 100);
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // URL을 클릭 가능한 링크로 변환
    const convertUrlsToLinks = (text) => {
        const urlPattern = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+|www\.[^\s<>"{}|\\^`\[\]]+\.[a-z]{2,}(?:\/[^\s<>"{}|\\^`\[\]]*)?)/gi;

        return text.replace(urlPattern, (match) => {
            let url = match;
            if (match.toLowerCase().startsWith('www.')) {
                url = 'http://' + match;
            }
            try {
                new URL(url);
                return `<a href="${url}" target="_blank" rel="noopener noreferrer">${match}</a>`;
            } catch (e) {
                return match;
            }
        });
    };

    // 메시지 추가
    const addMessage = (content, sender, isError = false) => {
        let escapedContent = content
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');

        escapedContent = convertUrlsToLinks(escapedContent);
        escapedContent = escapedContent.replace(/\n/g, '<br>');

        // 봇 메시지인 경우 현재 주제의 URL 정보 추가
        // if (sender === 'bot' && !isError) {
        //     const topicData = topicsData[selectedTopic];
        //     if (topicData && topicData.url) {
        //         escapedContent += `<div style="margin-top: 0px; text-align: left;">
        //             <a href="${topicData.url}" target="_blank" rel="noopener noreferrer" style="display: inline-block; color: #3b82f6 !important; text-decoration: none !important; font-weight: 500; padding: 8px 16px; padding-top: 0px; background-color: rgba(59, 130, 246, 0.08); border-radius: 20px; transition: all 0.3s ease; border: 1px solid rgba(59, 130, 246, 0.15); font-size: 14px;">
        //                 ${topicData.name} - 자세한 내용 보기
        //             </a>
        //         </div>`;
        //     }
        // }

        setMessages(prev => [...prev, { content: escapedContent, sender, isError }]);
    };

    // API 호출
    const sendToAPI = async (question) => {
        const hostUri = process.env.REACT_APP_CHATBOT_URL;
        const response = await fetch(hostUri, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                question: question,
                topic: selectedTopic,
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.error) {
            throw new Error(data.error);
        }

        return data.result || '응답을 받을 수 없습니다.';
    };

    // 메시지 전송
    const handleSubmit = async (e, directQuestion = null) => {
        if (e) e.preventDefault();

        const question = directQuestion || inputValue.trim();
        if (!question) return;

        addMessage(question, 'user');
        setInputValue('');
        setIsLoading(true);

        try {
            const response = await sendToAPI(question);
            addMessage(response, 'bot');
        } catch (error) {
            addMessage('죄송합니다. 오류가 발생했습니다. 다시 시도해 주세요.', 'bot', true);
            console.error('API Error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // 즐겨찾는 질문 클릭
    const handleFavoriteClick = (question) => {
        setSidebarOpen(false);
        handleSubmit(null, question);
    };

    // 주제 변경
    const handleTopicChange = (newTopic) => {
        setSelectedTopic(newTopic);
        const topicData = topicsData[newTopic];
        if (topicData) {
            addMessage(`${topicData.name} 주제가 선택되었습니다. 관련 질문을 해보세요!`, 'bot');
        }
    };

    // 도움말 클릭
    const handleHelpClick = () => {
        const helpMessage = `🤖 도움말\n\n📂 주제 선택: 원하는 주제를 선택하세요\n⭐ 즐겨찾는 질문: 아이콘을 클릭하여 자주 묻는 질문을 확인하세요\n💬 채팅: 궁금한 내용을 직접 입력하여 질문하세요\n\n더 궁금한 점이 있으면 언제든 질문해주세요!`;
        addMessage(helpMessage, 'bot');
    };

    if (!isOpen) return null;

    const currentTopicData = topicsData[selectedTopic];

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
        }} onClick={onClose}>
            <div className="chatbot-modal" onClick={(e) => e.stopPropagation()}>

                {/* Header */}
                <div className="chatbot-header">
                    <div className="chatbot-header-left">
                        <div className="chatbot-icon chatbot-icon-star" onClick={() => setSidebarOpen(!sidebarOpen)} title="즐겨찾기">
                            ⭐
                        </div>
                        <select
                            value={selectedTopic}
                            onChange={(e) => handleTopicChange(e.target.value)}
                            className="chatbot-topic-select"
                        >
                            {Object.keys(topicsData).map(topicId => (
                                <option key={topicId} value={topicId} style={{ background: '#3b82f6', color: 'white' }}>
                                    {topicsData[topicId].name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="chatbot-header-center">
                        AI 시스템관리 챗봇
                    </div>
                    <div className="chatbot-header-right">
                        <div className="chatbot-icon chatbot-icon-help" onClick={handleHelpClick} title="도움말">
                            ❓
                        </div>
                        <div className="chatbot-icon chatbot-icon-close" onClick={onClose} title="닫기">
                            ✕
                        </div>
                    </div>
                </div>

                {/* Sidebar */}
                {sidebarOpen && (
                    <div style={{
                        width: '100%',
                        background: '#f8fafc',
                        borderBottom: '1px solid #e2e8f0',
                        display: 'flex',
                        flexDirection: 'row',
                        minHeight: '120px',
                    }}>
                        <div style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '20px',
                            padding: '15px 20px',
                            alignItems: 'flex-start',
                        }}>
                            <div style={{ flex: 1 }}>
                                <h3 style={{
                                    marginBottom: '8px',
                                    color: '#0f172a',
                                    fontSize: '14px',
                                    padding: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}>⭐ 즐겨찾는 질문</h3>
                                <div style={{
                                    background: 'white',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    maxHeight: '240px',
                                    overflowY: 'auto',
                                }}>
                                    {currentTopicData.favorites.map((question, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                background: 'white',
                                                borderBottom: index < currentTopicData.favorites.length - 1 ? '1px solid #e2e8f0' : 'none',
                                                padding: '12px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                lineHeight: '1.4',
                                            }}
                                            onClick={() => handleFavoriteClick(question)}
                                            onMouseEnter={(e) => {
                                                e.target.style.background = '#eff6ff';
                                                e.target.style.transform = 'translateY(-1px)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.background = 'white';
                                                e.target.style.transform = 'translateY(0)';
                                            }}
                                        >
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Chat Messages */}
                <div ref={chatMessagesRef} className="chatbot-messages">
                    {messages.map((msg, index) => (
                        <div key={index} className={`chatbot-message ${msg.sender === 'user' ? 'chatbot-message-user' : 'chatbot-message-bot'}`}>
                            <div
                                className={`chatbot-message-content ${msg.sender === 'user' ? 'chatbot-message-content-user' : 'chatbot-message-content-bot'} ${msg.isError ? 'chatbot-message-error' : ''}`}
                                dangerouslySetInnerHTML={{ __html: msg.content }}
                            />
                        </div>
                    ))}
                    {isLoading && (
                        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '20px' }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '15px 20px',
                                background: 'white',
                                border: '1px solid #e0e0e0',
                                borderRadius: '20px',
                                borderBottomLeftRadius: '5px',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                maxWidth: '70%',
                            }}>
                                <span>AI가 생각 중입니다</span>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                    {[0, 1, 2].map(i => (
                                        <div key={i} style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            background: '#3b82f6',
                                            animation: 'loading 1.4s ease-in-out infinite both',
                                            animationDelay: `${-0.32 + i * 0.16}s`,
                                        }} />
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Chat Input */}
                <div className="chatbot-input-container">
                    <form onSubmit={handleSubmit} className="chatbot-input-form">
                        <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder="메시지를 입력하세요..."
                            className="chatbot-input"
                        />
                        <button
                            type="submit"
                            disabled={isLoading}
                            className={`chatbot-send-button ${isLoading ? 'chatbot-send-button-loading' : ''}`}
                        >
                            {isLoading ? '전송 중...' : '전송'}
                        </button>
                    </form>
                </div>

                <style>{`
                    .chatbot-modal {
                        display: flex;
                        flex-direction: column;
                        height: 90vh;
                        max-width: 1200px;
                        width: 90%;
                        margin: 0 auto;
                        background: white;
                        box-shadow: 0 0 20px rgba(0,0,0,0.3);
                        border-radius: 10px;
                        overflow: hidden;
                    }

                    .chatbot-header {
                        background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                        color: white;
                        padding: 20px 30px;
                        display: grid;
                        grid-template-columns: minmax(200px, 1fr) auto minmax(60px, 1fr);
                        align-items: center;
                        gap: 30px;
                    }

                    .chatbot-header-left {
                        display: flex;
                        align-items: center;
                        gap: 30px;
                        justify-self: start;
                    }

                    .chatbot-header-center {
                        justify-self: center;
                        text-align: center;
                        font-size: 24px;
                        font-weight: 700;
                    }

                    .chatbot-header-right {
                        display: flex;
                        align-items: center;
                        justify-self: end;
                        gap: 10px;
                    }

                    .chatbot-icon {
                        width: 40px;
                        height: 40px;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 18px;
                        cursor: pointer;
                        transition: transform 0.2s ease;
                    }

                    .chatbot-icon:hover {
                        transform: scale(1.1);
                    }

                    .chatbot-icon-star {
                        background: #FFD700;
                        color: #333;
                        box-shadow: 0 2px 8px rgba(255, 215, 0, 0.3);
                    }

                    .chatbot-icon-help, .chatbot-icon-close {
                        background: rgba(255, 255, 255, 0.2);
                        color: white;
                        border: 2px solid rgba(255, 255, 255, 0.3);
                    }

                    .chatbot-topic-select {
                        background: rgba(255, 255, 255, 0.2);
                        border: 2px solid rgba(255, 255, 255, 0.3);
                        border-radius: 20px;
                        color: white;
                        padding: 8px 15px;
                        font-size: 13px;
                        cursor: pointer;
                        min-width: 120px;
                    }

                    .chatbot-messages {
                        flex: 1;
                        padding: 20px;
                        overflow-y: auto;
                        overflow-x: hidden;
                        background-color: #f8fafc;
                        min-height: 0;
                    }

                    .chatbot-message {
                        margin-bottom: 20px;
                        display: flex;
                    }

                    .chatbot-message-user {
                        justify-content: flex-end;
                    }

                    .chatbot-message-bot {
                        justify-content: flex-start;
                    }

                    .chatbot-message-content {
                        max-width: 70%;
                        padding: 12px 20px;
                        border-radius: 20px;
                        font-size: 14px;
                        line-height: 1.4;
                        word-wrap: break-word;
                        font-weight: 500;
                    }

                    .chatbot-message-content-user {
                        background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                        color: white;
                        border-bottom-right-radius: 5px;
                    }

                    .chatbot-message-content-bot {
                        background: white;
                        color: #0f172a;
                        border: 1px solid #e2e8f0;
                        border-bottom-left-radius: 5px;
                        box-shadow: 0 2px 5px rgba(0,0,0,0.08);
                    }

                    .chatbot-message-error {
                        background: #ffebee !important;
                        color: #c62828 !important;
                        border-color: #ffcdd2 !important;
                    }

                    .chatbot-input-container {
                        padding: 20px;
                        background: white;
                        border-top: 1px solid #e2e8f0;
                        box-shadow: 0 -2px 10px rgba(0,0,0,0.08);
                    }

                    .chatbot-input-form {
                        display: flex;
                        gap: 10px;
                    }

                    .chatbot-input {
                        flex: 1;
                        padding: 15px 20px;
                        border: 2px solid #e2e8f0;
                        border-radius: 25px;
                        font-size: 16px;
                        outline: none;
                        transition: border-color 0.3s ease;
                    }

                    .chatbot-input:focus {
                        border-color: #3b82f6;
                    }

                    .chatbot-send-button {
                        padding: 15px 25px;
                        background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                        color: white;
                        border: none;
                        border-radius: 25px;
                        font-size: 16px;
                        font-weight: 600;
                        cursor: pointer;
                        transition: transform 0.2s ease;
                    }

                    .chatbot-send-button:hover {
                        transform: translateY(-2px);
                    }

                    .chatbot-send-button-loading {
                        background: #ccc !important;
                        cursor: not-allowed !important;
                        transform: none !important;
                    }

                    @keyframes loading {
                        0%, 80%, 100% { transform: scale(0); }
                        40% { transform: scale(1); }
                    }

                    /* 모바일 반응형 (768px 이하) */
                    @media (max-width: 768px) {
                        .chatbot-modal {
                            width: 95%;
                            height: 95vh;
                            border-radius: 8px;
                        }

                        .chatbot-header {
                            padding: 15px 20px;
                            gap: 15px;
                            grid-template-columns: 1fr auto 1fr;
                        }

                        .chatbot-header-left {
                            gap: 15px;
                        }

                        .chatbot-header-center {
                            font-size: 18px;
                        }

                        .chatbot-icon {
                            width: 36px;
                            height: 36px;
                            font-size: 16px;
                        }

                        .chatbot-topic-select {
                            font-size: 12px;
                            padding: 6px 12px;
                            min-width: 100px;
                        }

                        .chatbot-messages {
                            padding: 15px;
                        }

                        .chatbot-message {
                            margin-bottom: 15px;
                        }

                        .chatbot-message-content {
                            max-width: 80%;
                            font-size: 13px;
                            padding: 10px 16px;
                        }

                        .chatbot-input-container {
                            padding: 15px;
                        }

                        .chatbot-input {
                            padding: 12px 16px;
                            font-size: 14px;
                        }

                        .chatbot-send-button {
                            padding: 12px 20px;
                            font-size: 14px;
                        }
                    }

                    /* 작은 모바일 (480px 이하) */
                    @media (max-width: 480px) {
                        .chatbot-modal {
                            width: 100%;
                            height: 100vh;
                            border-radius: 0;
                        }

                        .chatbot-header {
                            padding: 12px 15px;
                            gap: 10px;
                        }

                        .chatbot-header-left {
                            gap: 10px;
                        }

                        .chatbot-header-center {
                            font-size: 15px;
                        }

                        .chatbot-icon {
                            width: 32px;
                            height: 32px;
                            font-size: 14px;
                        }

                        .chatbot-topic-select {
                            font-size: 11px;
                            padding: 5px 10px;
                            min-width: 80px;
                        }

                        .chatbot-messages {
                            padding: 12px;
                        }

                        .chatbot-message {
                            margin-bottom: 12px;
                        }

                        .chatbot-message-content {
                            max-width: 85%;
                            font-size: 12px;
                            padding: 10px 14px;
                        }

                        .chatbot-input-container {
                            padding: 12px;
                        }

                        .chatbot-input-form {
                            gap: 8px;
                        }

                        .chatbot-input {
                            padding: 10px 14px;
                            font-size: 13px;
                            border-radius: 20px;
                        }

                        .chatbot-send-button {
                            padding: 10px 18px;
                            font-size: 13px;
                            border-radius: 20px;
                        }
                    }
                `}</style>
            </div>
        </div>
    );
};

export default CompChatbot;
